// src/utils/valuationDisclosure.js
// Règles d'affichage des valeurs (rapport Artprice 2026 : « le dernier prix n'est pas la valeur ») :
// une estimation se montre avec sa DATE, son STATUT (indicative / validée par un expert),
// un badge « ancienne » au-delà de 12 mois, et une mention de pied sur les dossiers.
export const STALE_MONTHS = 12; // seuil à confirmer avec Vanessa

const LANGS = ["fr", "en", "de"];
const pick = (lang) => (LANGS.includes(String(lang || "fr").slice(0, 2)) ? String(lang).slice(0, 2) : "fr");

export function valuationDate(a) {
  return a?.last_valuation_date || a?.expert_date || a?.last_estimation_date || null;
}

export function isStaleValuation(date, months = STALE_MONTHS) {
  if (!date) return false;
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return false;
  const limit = new Date();
  limit.setMonth(limit.getMonth() - months);
  return d < limit;
}

// « expert » seulement si une validation humaine est enregistrée ; sinon « indicative ». Jamais mélangés.
export function valuationStatus(a) {
  return a?.valuation_validated_by ? "expert" : "indicative";
}

const TEXT = {
  fr: {
    indicative: "Indicative", expert: "Validée par un expert",
    estimatedOn: "Estimée le", noDate: "Date de l'estimation non renseignée",
    stale: "Estimation ancienne, à réactualiser",
    caption: "Valeur indicative à une date donnée, pas un prix garanti.",
    staleCount: (n) => `${n} estimation${n > 1 ? "s" : ""} non datée${n > 1 ? "s" : ""} ou ancienne${n > 1 ? "s" : ""} (plus de 12 mois).`,
    footer: (d) => `Valorisation indicative au ${d}. Elle ne constitue pas une expertise et ne garantit pas un prix de vente.`,
  },
  en: {
    indicative: "Indicative", expert: "Validated by an expert",
    estimatedOn: "Estimated on", noDate: "Estimate date not provided",
    stale: "Old estimate, to be updated",
    caption: "Indicative value at a given date, not a guaranteed price.",
    staleCount: (n) => `${n} undated or old estimate${n > 1 ? "s" : ""} (over 12 months).`,
    footer: (d) => `Indicative valuation as of ${d}. It is not an appraisal and does not guarantee a sale price.`,
  },
  de: {
    indicative: "Indikativ", expert: "Von einem Experten bestätigt",
    estimatedOn: "Geschätzt am", noDate: "Datum der Schätzung nicht angegeben",
    stale: "Ältere Schätzung, zu aktualisieren",
    caption: "Indikativer Wert zu einem bestimmten Datum, kein garantierter Preis.",
    staleCount: (n) => `${n} undatierte oder alte Schätzung${n > 1 ? "en" : ""} (älter als 12 Monate).`,
    footer: (d) => `Indikative Bewertung zum ${d}. Sie stellt kein Gutachten dar und garantiert keinen Verkaufspreis.`,
  },
};

export const valuationText = (lang) => TEXT[pick(lang)];

const LOCALE = { fr: "fr-FR", en: "en-GB", de: "de-DE" };
export const formatValuationDate = (d, lang) =>
  d ? new Date(d).toLocaleDateString(LOCALE[pick(lang)]) : "";

// Mention de pied des dossiers (assurance, succession, vente) : date = plus récente des estimations.
export function valuationFooter(artworks = [], lang = "fr") {
  const dates = (Array.isArray(artworks) ? artworks : [artworks])
    .map(valuationDate).filter(Boolean).map((d) => new Date(d)).filter((d) => !Number.isNaN(d.getTime()));
  const latest = dates.length ? new Date(Math.max(...dates)) : new Date();
  return valuationText(lang).footer(latest.toLocaleDateString(LOCALE[pick(lang)]));
}

// Résumé lisible d'une estimation pour un PDF ou une liste : fourchette · date · statut · ancienneté.
// fmtMoney : (nombre) => "12 000 €" ; sans estimation datée, la date manquante est signalée.
export function valuationSummary(a, fmtMoney, lang = "fr") {
  const tx = valuationText(lang);
  const lo = Number(a?.valuation_low), hi = Number(a?.valuation_high);
  const parts = [];
  if (lo || hi) parts.push(`${lo ? fmtMoney(lo) : "—"} – ${hi ? fmtMoney(hi) : "—"}`);
  const d = valuationDate(a);
  parts.push(d ? `${tx.estimatedOn} ${formatValuationDate(d, lang)}` : tx.noDate);
  parts.push(tx[valuationStatus(a)]);
  if (isStaleValuation(d)) parts.push(tx.stale);
  return parts.join(" · ");
}
